import { computed, action, makeObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { TrpcClient } from 'src/appState/TrpcClient';
import { getSavedCards, getSavedCardsWithNetDeposits } from 'src_server/trpc/types/payments';
import { PaymentProvidersType } from 'src/domains/layouts/config/features/types';
import { DateTime } from 'src_common/utils/time/time';
import { OpenBankingProvidersType } from 'src/domains/common/configFeatures/ConfigFeatures';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { WebsocketV1 } from 'src/domains/layouts/state/websocketV1/WebsocketV1';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { ApiResponseWrapper } from 'src_common/common/mobx-utils/ApiResponseWrapper';
import { Common } from 'src/domains/common/Common';

const isCardExpired = (card: getSavedCards.SavedCardInterfaceType): boolean => {
    return DateTime.current().isBefore(DateTime.from(card.expires ?? undefined) ?? DateTime.current());
};

//It works bad in backend. LastUpdatedAt return createdAtDate.
const sortByLastUpdatedAt = (
    a: getSavedCards.SavedCardInterfaceType,
    b: getSavedCards.SavedCardInterfaceType
): number => {
    const lastUpadedA = a.lastUsedAt ?? null;
    const lastUpadedB = b.lastUsedAt ?? null;

    if (lastUpadedA === null && lastUpadedB === null) {
        return 0;
    }

    if (lastUpadedA === null) {
        return -1;
    }

    if (lastUpadedB === null) {
        return 1;
    }

    return DateTime.from(lastUpadedA)?.isBefore(lastUpadedB) === true ? 1 : -1;
};

export class PaymentMethodDropdownState {
    public readonly selectedTopUpOption: FormInputState<string, string>;
    public readonly savedPaymentMethodResource: Resource<getSavedCards.ResponseType>;
    public readonly savedOpenBankingResource: Resource<getSavedCards.ResponseType | null>;
    public readonly savedCardsWithNetDepositsResponseWrapper: AutoMap<
        [number],
        ApiResponseWrapper<getSavedCardsWithNetDeposits.ResponseType | null>
    >;

    public constructor(
        private readonly trpcClient: TrpcClient,
        private readonly usePaymentFormForUniverse: PaymentProvidersType,
        private readonly useOpenBanking: OpenBankingProvidersType,
        private readonly basicDataModel: BasicDataModel,
        private readonly common: Common
    ) {
        makeObservable(this);
        this.selectedTopUpOption = FormInputState.new('');
        this.savedPaymentMethodResource = new Resource(async (): Promise<getSavedCards.ResponseType> => {
            return await this.trpcClient.client.payments.getSavedCard.query({
                provider: this.usePaymentFormForUniverse.type,
            });
        });

        this.savedOpenBankingResource = new Resource(async (): Promise<getSavedCards.ResponseType | null> => {
            if (this.useOpenBanking !== null) {
                return await this.trpcClient.client.payments.getSavedCard.query({
                    provider: this.useOpenBanking.type,
                });
            }
            return null;
        });

        this.savedCardsWithNetDepositsResponseWrapper = new AutoMap(
            ([userId]) =>
                new ApiResponseWrapper(
                    'getSavedCardsWithNetDeposits',
                    null,
                    async (): Promise<getSavedCardsWithNetDeposits.ResponseType> => {
                        const request = await this.trpcClient.client.payments.getSavedCardsWithNetDeposits.query({
                            provider: this.usePaymentFormForUniverse.type,
                        });

                        return request;
                    },
                    (refresh) => {
                        refresh();

                        WebsocketV1.get(this.common).subscribe(`${userId}:Wallet`, refresh);

                        return (): void => {
                            WebsocketV1.get(this.common).unsubscribe(`${userId}:Wallet`, refresh);
                        };
                    }
                )
        );
    }

    @action public refreshPaymentMethods = async (): Promise<void> => {
        if (this.common.features.withdrawDropdownNew === true) {
            const userId = this.common.session.userId;
            if (userId === null) {
                return;
            }
            this.savedCardsWithNetDepositsResponseWrapper.get([userId]).refresh();
        } else {
            await this.savedPaymentMethodResource.refresh();
        }
        if (this.useOpenBanking !== null) {
            await this.savedOpenBankingResource.refresh();
        }
    };

    @computed public get savedOpenBankingMethods(): getSavedCards.SuccessResponseType {
        if (this.basicDataModel.currency === 'EUR' && this.useOpenBanking?.isEuroAllowed === false) {
            return [];
        }

        const resource = this.savedOpenBankingResource.getReady();
        if (resource === null || resource.responseStatus === 'error') {
            return [];
        }

        return resource.response;
    }

    @computed public get savedPaymentMethods(): getSavedCards.SuccessResponseType | null {
        const resource = this.savedPaymentMethodResource.getReady();
        if (resource === null || resource.responseStatus === 'error') {
            return null;
        }

        return resource.response;
    }

    @computed public get savedPaymentMethodsWithNetDeposits(): getSavedCardsWithNetDeposits.SuccessResponseType | null {
        const userId = this.common.session.userId;
        if (userId === null) {
            return [];
        }
        const request = this.savedCardsWithNetDepositsResponseWrapper.get([userId]).data;
        if (request !== null && request.responseStatus === 'success') {
            return request.response;
        }

        return null;
    }

    @computed public get paymentMethodsValue(): string {
        const selectedPaymentMethod = this.selectedPaymentMethod;
        if (selectedPaymentMethod === undefined) {
            return 'none';
        }

        return selectedPaymentMethod.id;
    }

    @computed public get selectedPaymentMethod(): getSavedCards.SavedCardInterfaceType | undefined {
        if (this.common.features.withdrawDropdownNew === true) {
            if (this.savedPaymentMethodsWithNetDeposits === null) {
                return undefined;
            }

            if (this.selectedTopUpOption.value === '') {
                const noExpiredPaymentMethods = [...this.savedPaymentMethodsWithNetDeposits]
                    .filter((paymentMethod) => isCardExpired(paymentMethod))
                    .map(({ netDeposit, ...card }) => card)
                    .sort(sortByLastUpdatedAt);
                return noExpiredPaymentMethods[0] ?? undefined;
            }

            const accountSavedPaymentMethods = this.savedPaymentMethodsWithNetDeposits.map(
                ({ netDeposit, ...card }) => card
            );
            for (const paymentMethod of accountSavedPaymentMethods) {
                if (paymentMethod.id === this.selectedTopUpOption.value) {
                    return paymentMethod;
                }
            }
        } else {
            if (this.savedPaymentMethods === null) {
                return undefined;
            }

            if (this.selectedTopUpOption.value === '') {
                const noExpiredPaymentMethods = [...this.savedPaymentMethods]
                    .filter((paymentMethod) => isCardExpired(paymentMethod))
                    .sort(sortByLastUpdatedAt);
                return noExpiredPaymentMethods[0] ?? undefined;
            }

            const accountSavedPaymentMethods = this.savedPaymentMethods;
            for (const paymentMethod of accountSavedPaymentMethods) {
                if (paymentMethod.id === this.selectedTopUpOption.value) {
                    return paymentMethod;
                }
            }
        }
        return undefined;
    }

    @action public handlePaymentSelectChange = (value: string): void => {
        this.selectedTopUpOption.setValue(value);
        this.selectedTopUpOption.setAsVisited();
    };

    @computed public get isNewCard(): boolean {
        return this.paymentMethodsValue === 'none';
    }

    @computed public get areSavedPaymentMethods(): boolean {
        if (this.common.features.withdrawDropdownNew === true) {
            return this.savedPaymentMethodsWithNetDeposits !== null &&
                this.savedPaymentMethodsWithNetDeposits.length > 0
                ? true
                : false;
        }
        return this.savedPaymentMethods !== null && this.savedPaymentMethods.length > 0 ? true : false;
    }
}
